import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/supabase.config';

// Materialized view types
interface MinisterStatsView {
  total_ministers: number;
  new_ministers_this_year: number;
  credentialized_ministers_this_year: number;
  active_ministers: number;
  last_minister_added: string;
}

interface RequestStatsView {
  total_requests: number;
  pending_requests: number;
  approved_requests: number;
  rejected_requests: number;
  requests_this_year: number;
  last_request_created: string;
}

interface RequestsByStatusView {
  status: string;
  count: number;
}

interface MinistersByDistrictView {
  district_id: string;
  district: string;
  count: number;
  rank: number;
}

interface MinistersByDegreeView {
  id: string;
  degree: string;
  count: number;
  rank: number;
}

interface RegionsWithoutRequestsView {
  region_id: string;
  region: string;
  district: string;
  total_count: number;
}

export interface DashboardStats {
  totalMinisters: number;
  activeRequests: number;
  pendingApprovals: number;
  districtsActive: number;
  completedRequests: number;
  totalRequests: number;
  monthlyGrowth: number;
  credentializedMinisters: number;
  ministersWithoutCredentials: number;
  requestsByStatus: { status: string; count: number; fill: string }[];
  ministersByDistrict: { district: string; count: number; fill: string }[];
  ministersByDegree: { degree: string; count: number; fill: string }[];
  regionsWithNoRequests: { region: string; district: string }[];
}

async function fetchDashboardStats(): Promise<DashboardStats> {
  // Fetch all views in parallel for maximum performance
  const [
    ministerStatsResult,
    requestStatsResult,
    requestsByStatusResult,
    ministersByDistrictResult,
    ministersByDegreeResult,
    regionsWithoutRequestsResult,
    districtsActiveResult,
  ] = await Promise.all([
    supabase.from('dashboard_minister_stats').select('*').single(),
    supabase.from('dashboard_request_stats').select('*').single(),
    supabase.from('dashboard_requests_by_status').select('*'),
    supabase.from('dashboard_ministers_by_district').select('*'),
    supabase.from('dashboard_ministers_by_degree').select('*'),
    supabase.from('dashboard_regions_without_requests').select('*'),
    supabase.from('districts').select('*', { count: 'exact', head: true }).eq('is_active', true),
  ]);

  // Extract data
  const ministerStats = ministerStatsResult.data as MinisterStatsView | null;
  const requestStats = requestStatsResult.data as RequestStatsView | null;
  const requestsByStatusData = requestsByStatusResult.data as RequestsByStatusView[] || [];
  const ministersByDistrictData = ministersByDistrictResult.data as MinistersByDistrictView[] || [];
  const ministersByDegreeData = ministersByDegreeResult.data as MinistersByDegreeView[] || [];
  const regionsWithoutRequestsData = regionsWithoutRequestsResult.data as RegionsWithoutRequestsView[] || [];
  const districtsActive = districtsActiveResult.count || 0;

  // Process data for frontend
  const totalMinisters = ministerStats?.total_ministers || 0;
  const credentializedMinisters = ministerStats?.credentialized_ministers_this_year || 0;
  const newMinistersThisYear = ministerStats?.new_ministers_this_year || 0;
  const ministersWithoutCredentials = totalMinisters - credentializedMinisters;
  const monthlyGrowth = totalMinisters ? (newMinistersThisYear / totalMinisters) * 100 : 0;

  const totalRequests = requestStats?.requests_this_year || 0;
  const pendingApprovals = requestStats?.pending_requests || 0;

  // Calculate active requests (not DRAFT or REJECTED) from status breakdown
  const activeRequests = requestsByStatusData
    .filter(item => item.status !== 'DRAFT' && item.status !== 'REJECTED')
    .reduce((sum, item) => sum + item.count, 0);

  const completedRequests = requestsByStatusData.find(item => item.status === 'DELIVERED')?.count || 0;

  // Spanish labels for status
  const statusLabels: Record<string, string> = {
    'DRAFT': 'Borrador',
    'SUBMITTED': 'Enviado',
    'APPROVED': 'Aprobado',
    'REJECTED': 'Rechazado',
    'PRINTED': 'Impreso',
    'DELIVERED': 'Entregado',
  };

  // Theme colors for charts (using actual HSL values for ECharts compatibility)
  const statusColors: Record<string, string> = {
    'DRAFT': 'hsl(215, 20%, 65%)',         // Gray for drafts
    'SUBMITTED': 'hsl(48, 96%, 53%)',      // Bright Yellow for submitted
    'APPROVED': 'hsl(142, 71%, 45%)',      // Green for approved
    'REJECTED': 'hsl(0, 84%, 60%)',        // Red for rejected
    'PRINTED': 'hsl(190, 95%, 55%)',       // Cyan for printed
    'DELIVERED': 'hsl(217, 91%, 60%)',     // Blue for delivered
  };

  // Create a map of existing status counts
  const statusCountMap: Record<string, number> = {};
  requestsByStatusData.forEach(item => {
    statusCountMap[item.status] = item.count;
  });

  // Ensure all status types appear in the chart (with 0 if missing)
  const allStatuses = ['DRAFT', 'SUBMITTED', 'APPROVED', 'REJECTED', 'PRINTED', 'DELIVERED'];
  const requestsByStatus = allStatuses.map(status => ({
    status: statusLabels[status] || status,
    count: statusCountMap[status] || 0,
    fill: statusColors[status] || 'hsl(var(--primary))',
  }));

  // Color palette for bar charts
  const colors = [
    'hsl(190 95% 55%)',      // Cyan/Primary bright
    'hsl(353 100% 65%)',     // Coral/Secondary
    'hsl(45 95% 55%)',       // Gold/Accent
    'hsl(160 75% 45%)',      // Green/Success
    'hsl(270 70% 60%)',      // Purple
    'hsl(15 85% 60%)',       // Orange
    'hsl(200 80% 50%)',      // Blue variant
    'hsl(340 90% 55%)',      // Pink variant
    'hsl(85 60% 50%)',       // Lime
    'hsl(280 60% 55%)',      // Violet
  ];

  const ministersByDistrict = ministersByDistrictData.map((item, index) => ({
    district: item.district,
    count: item.count,
    fill: colors[index % colors.length],
  }));

  const ministersByDegree = ministersByDegreeData.map((item, index) => ({
    degree: item.degree,
    count: item.count,
    fill: colors[index % colors.length],
  }));

  const regionsWithNoRequests = regionsWithoutRequestsData.map(item => ({
    region: item.region,
    district: item.district,
  }));

  console.log('=== Dashboard Stats (Materialized Views) ===', {
    totalMinisters,
    credentializedMinisters,
    ministersWithoutCredentials,
    districtsActive,
    activeRequests,
    pendingApprovals,
    totalRequests,
    completedRequests,
    requestsByStatus: requestsByStatus.length,
    ministersByDistrict: ministersByDistrict.length,
    ministersByDegree: ministersByDegree.length,
    regionsWithNoRequests: regionsWithNoRequests.length,
  });

  return {
    totalMinisters,
    activeRequests,
    pendingApprovals,
    districtsActive,
    completedRequests,
    totalRequests,
    monthlyGrowth: parseFloat(monthlyGrowth.toFixed(1)),
    credentializedMinisters,
    ministersWithoutCredentials,
    requestsByStatus,
    ministersByDistrict,
    ministersByDegree,
    regionsWithNoRequests,
  };
}

export function useDashboardStats() {
  return useQuery({
    queryKey: ['dashboardStats'],
    queryFn: fetchDashboardStats,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
  });
}
